/**
 * `/credits` — the balance, and the ledger that explains how it got there.
 *
 * **Three things this screen must get right, because a credit system that gets
 * any of them wrong reads as a billing dispute:**
 *
 *   1. **Accepted is not delivered, and neither is consumed.** `reserved` is
 *      credits held by messages still in flight; `balance` only moves when one
 *      is actually delivered or a human corrects it. The two numbers are shown
 *      separately, never folded into one, because folding them is exactly how a
 *      reservation gets read as a consumption.
 *   2. **A bounce did not consume a credit.** Every `release` row on the ledger
 *      moves `reserved` only and leaves `balance` alone, because that is what
 *      `LEDGER_BALANCE_EFFECTS` (imported from `@wend/shared`, never re-derived)
 *      says a `release` does.
 *   3. **Credits are Wend's own metering unit, not money.** Under BYO the tenant
 *      pays AWS — and, from a later release, Meta — directly for what actually
 *      happens on their own account, in amounts Wend never sees. Nothing on this
 *      screen says spend, cost, charge or price, and no figure carries a
 *      currency symbol.
 *
 * **A tenant cannot grant themselves credits, and this screen offers no control
 * that suggests they can.** `CreditsGrantNote` says so in one line where an
 * "Add credits" button would otherwise sit. The rejected alternative is that
 * button, disabled or opening an explanatory dialogue: an affordance that cannot
 * work is worse than an absence, because it is pressed, refused, and then the
 * rest of the screen is distrusted.
 *
 * **Slice 17 moved the copy out and cut what was left.** Everything a person
 * needed a reason for — why the balance cache is authoritative, why a ledger is
 * append-only, what the two columns of "Moved" are — was prose written for the
 * next builder, and it is in this header and in `CreditsCells.tsx`'s now. What
 * stayed on screen is the part a reader would draw a false conclusion without:
 * a release is not a charge, an absent balance is not a zero, and a credit count
 * means nothing without the messages it buys. The pieces that carry those
 * sentences moved to `CreditsCells.tsx`, which imports no API client, so
 * `scripts/verify-credits-screen.tsx` can render them; this file kept the
 * layout, the fetches and the filters, which are not claims.
 *
 * Every number is the API's, read on the request that drew this screen:
 * `credit_balances` for the top panel, `credit_ledger` for the history below it.
 * Nothing here sums a page of the ledger to double-check the balance — that
 * comparison belongs to the background reconciliation, over the whole table, not
 * to this screen, over one page of it.
 *
 * **It runs hourly, and this header promised it once a night until slice 17.**
 * The interval is `RECONCILE_INTERVAL_MS` in
 * `credit-reconciliation.scheduler.ts` and it is `60 * 60_000`; that file's own
 * header argues for the hour on the grounds that drift is *money being wrong*.
 * The wrong word was off by a factor of twelve in the dangerous direction, and
 * it did the damage a stale justification does: slice 17 deleted the same false
 * period from a customer-facing callout in `CampaignResultsPanel.tsx` and left
 * it standing in this header, in a paragraph it rewrote in the same pass. A
 * reader who believes a wrong balance can sit unchecked until morning is one
 * argument away from adding the client-side re-sum this paragraph exists to
 * forbid, or from telling a tenant with a disputed balance to come back
 * tomorrow.
 *
 * The wrong word is deliberately not written out anywhere in this file, so that
 * `verify-page-hardening.tsx` can ban it flatly across the two credit modules
 * rather than having to tell a quotation from a claim. That check is the only
 * one in that suite that reads comments instead of stripping them, because here
 * the comment *is* the surface a defect lives on.
 *
 * No interval is named on the screen at all, and that is deliberate rather than
 * an omission: a number in the copy is a promise this app cannot keep — the
 * scheduler is the API's and can be retuned without anyone opening this file —
 * so the callout says a reconciliation runs regularly and leaves the period to
 * the one place that sets it.
 */
import {
  CHANNEL_VALUES,
  DEFAULT_PAGE_SIZE,
  type Channel,
  type CreditLedgerEntry,
  type LedgerEntryType,
} from '@wend/shared';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Spinner,
  type DataTableColumn,
} from '@wend/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { fetchCreditBalances, listCreditLedger } from '../../lib/api/credits';
import { errorMessage } from '../../lib/api/errors';
import { LEDGER_ENTRY_DISPLAY } from '../../lib/credit-display';
import { formatDateTime } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import {
  CreditsChannelBalance,
  CreditsGrantNote,
  LedgerAmountCell,
  LedgerFilterChips,
  LedgerMeaningCell,
  ledgerChannelOptions,
  ledgerTypeOptions,
} from './CreditsCells';

export function CreditsPage() {
  return (
    <AppLayout title="Credits">
      <div className="flex flex-col gap-6">
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Credits are Wend&rsquo;s metering unit, not money. Your own Amazon and Meta accounts bill
          you separately for what they actually do, and Wend never sees those amounts.
        </p>

        <BalancesPanel />
        <LedgerPanel />
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Balance                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Space held for the balances while they load, at roughly 7.5rem per channel.
 *
 * A spinner in a box a quarter the height of what replaces it is why a screen
 * jumps when it resolves, and this panel resolves into the number the whole page
 * is about — a reader who has started reading the ledger below should not have
 * it shoved down the screen underneath them.
 *
 * It is an approximation and is stated as one: the real height depends on how
 * many lines a caption wraps to at the reader's width, and the alternative — a
 * skeleton that reproduces the three figures exactly — is a second copy of the
 * layout that would have to be kept in step with the first. `min-height` rather
 * than `height`, so an underestimate grows rather than clipping. It scales off
 * `CHANNEL_VALUES` so a third channel does not leave the placeholder short.
 */
const BALANCE_SKELETON_HEIGHT = `${String(CHANNEL_VALUES.length * 7.5)}rem`;

function BalancesPanel() {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchCreditBalances(signal),
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Balance</CardTitle>
        <CardDescription>
          One row per channel. These are the numbers the send guard checks before a campaign goes
          out.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {loadingFirst ? (
          <div
            className="flex items-center gap-3 text-[0.92rem] text-muted"
            style={{ minHeight: BALANCE_SKELETON_HEIGHT }}
          >
            <Spinner size="md" label={null} />
            <span role="status">Loading this workspace&rsquo;s balance…</span>
          </div>
        ) : data === null ? (
          <ErrorState error={error} title="Could not load the balance" onRetry={reload} />
        ) : (
          <>
            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} What is shown below is the last version that loaded.
              </FormAlert>
            )}
            <div className="flex flex-col gap-6">
              {data.balances.map((balance) => (
                <CreditsChannelBalance key={balance.channel} balance={balance} />
              ))}
            </div>
          </>
        )}

        <CreditsGrantNote />
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Ledger                                                                      */
/* -------------------------------------------------------------------------- */

interface LedgerFilter {
  readonly channel: Channel | null;
  readonly type: LedgerEntryType | null;
}

const NO_LEDGER_FILTER: LedgerFilter = { channel: null, type: null };

function LedgerPanel() {
  const [filter, setFilter] = useState<LedgerFilter>(NO_LEDGER_FILTER);
  const [page, setPage] = useState(1);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listCreditLedger(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          ...(filter.channel === null ? {} : { channel: filter.channel }),
          ...(filter.type === null ? {} : { type: filter.type }),
        },
        signal,
      ),
    [filter.channel, filter.type, page],
  );

  const columns = useMemo<Array<DataTableColumn<CreditLedgerEntry>>>(
    () => [
      {
        key: 'createdAt',
        header: 'When',
        width: '11rem',
        cell: (entry) => (
          <span className="whitespace-nowrap text-muted">{formatDateTime(entry.createdAt)}</span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        width: '9rem',
        cell: (entry) => (
          <Badge tone={LEDGER_ENTRY_DISPLAY[entry.type].tone}>
            {LEDGER_ENTRY_DISPLAY[entry.type].label}
          </Badge>
        ),
      },
      {
        key: 'amount',
        header: 'Moved',
        width: '9rem',
        cell: (entry) => <LedgerAmountCell entry={entry} />,
      },
      {
        key: 'meaning',
        header: 'What happened',
        cell: (entry) => <LedgerMeaningCell entry={entry} />,
      },
      {
        key: 'campaign',
        header: 'Campaign',
        width: '10rem',
        cell: (entry) =>
          entry.campaignId === null ? (
            <span className="text-muted">—</span>
          ) : (
            <Link
              to={`/campaigns/${entry.campaignId}`}
              className="text-text underline-offset-4 hover:underline"
            >
              View campaign
            </Link>
          ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>History</CardTitle>
        <CardDescription>
          Append-only: a correction is a new row, never an edit. A released reservation moves
          reserved and never the balance.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failedToLoad ? (
          <ErrorState
            error={error}
            title="Could not load the credit history"
            onRetry={reload}
            className="flex flex-col items-start gap-3 py-2"
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4">
              <LedgerFilterChips<Channel>
                label="Filter by channel"
                value={filter.channel}
                options={ledgerChannelOptions()}
                onChange={(channel) => {
                  setFilter((current) => ({ ...current, channel }));
                  setPage(1);
                }}
              />
              <LedgerFilterChips<LedgerEntryType>
                label="Filter by type"
                value={filter.type}
                options={ledgerTypeOptions()}
                onChange={(type) => {
                  setFilter((current) => ({ ...current, type }));
                  setPage(1);
                }}
              />
              {refreshing && <Spinner size="sm" label="Updating the history" />}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The rows below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Credit history"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(entry) => entry.id}
              loading={loadingFirst}
              minWidth="52rem"
              empty={
                <p className="max-w-prose text-[0.9rem] text-muted">
                  {filter.channel === null && filter.type === null
                    ? 'Nothing has moved a credit yet. Reservations, deliveries and bounces appear here as they happen — send a campaign to start the ledger.'
                    : 'Nothing matches this filter. Clear it to see every entry.'}
                </p>
              }
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
